<template>
  <div>
    <div class="form-group m-b-10">
      <WdsInputCurrency
        v-model="internalModelValue"
        :config="desiredCurrencyConfig"
        :disabled="disabled"
      />
      <input :id="inputId" :name="inputName" :value="internalModelValue" type="hidden" />
    </div>
    <div v-if="showConversionHelper" :class="helperClassName">
      <WdsInputCurrency
        v-model="helperValue"
        :class="inputClassName"
        :config="userCurrencyConfig"
        :disabled="disabled"
        :info="conversionHelperInputTitle"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue'
import { WdsInputCurrency } from '@worksome/wds-components'
import { useTranslation } from '@/composables/translation'

/**
 * This component works in submittable forms (taking in an initialValue)
 * OR with v-model
 * note: for v-model to work, there is a :value watcher to reset the initial state of the component
 */

const props = defineProps({
  desiredCurrencyCode: { type: String, required: true },
  userCurrencyCode: { type: String, required: true },
  initialValue: { type: String },
  modelValue: { type: String },
  inputId: { type: String, default: '' },
  inputName: { type: String, default: '' },
  inputClassName: { type: String, default: '' },
  helperClassName: { type: String, default: '' },
  helperInputLabel: { type: String, required: true },
  globalChangeFn: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const { $t } = useTranslation()

const currentValue = ref(props.modelValue || props.initialValue || '0.00')

const desiredCurrencyConfig = {
  currencySymbol: props.desiredCurrencyCode,
  thousandsSeparator: window.currentLocale.thousandsSeparator,
  decimalSeparator: window.currentLocale.decimalSeparator,
  showFractions: true,
}

const userCurrencyConfig = {
  currencySymbol: props.userCurrencyCode,
  thousandsSeparator: window.currentLocale.thousandsSeparator,
  decimalSeparator: window.currentLocale.decimalSeparator,
  showFractions: true,
}

function convertCurrency(amount, from, to) {
  const { exchangeRates } = window
  return (Number(amount) * (Number(exchangeRates[to]) / Number(exchangeRates[from]))).toFixed(2)
}

const internalModelValue = computed({
  get() {
    return currentValue.value
  },
  set(value) {
    currentValue.value = value
    emit('update:modelValue', value)
    if (props.globalChangeFn && typeof window[props.globalChangeFn] !== 'undefined') {
      window[props.globalChangeFn](Number(value))
    }
  },
})

const helperValue = computed({
  get() {
    return convertCurrency(
      internalModelValue.value,
      props.desiredCurrencyCode,
      props.userCurrencyCode,
    )
  },
  set(value) {
    internalModelValue.value = convertCurrency(
      value,
      props.userCurrencyCode,
      props.desiredCurrencyCode,
    )
  },
})

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal !== currentValue.value) {
      currentValue.value = newVal
    }
  },
)

const showConversionHelper = computed(
  () => props.desiredCurrencyCode !== props.userCurrencyCode && helperValue.value > 0,
)

const conversionHelperInputTitle = computed(
  () => `* ${$t('Approximate conversion based on your local currency')}`,
)
</script>
